
import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../AppContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion } from 'framer-motion';
import ChallengePageSkeleton from '../components/Showdown/ChallengePageSkeleton';
import { Zap } from 'lucide-react';
import TauntSelectionModal from '../components/Showdown/TauntSelectionModal';

const ChallengePage = ({ startChallenge }) => {
    const { db, user } = useAppContext();
    const [dreamWeaves, setDreamWeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('souls');
    const [selectedDreamWeave, setSelectedDreamWeave] = useState(null); // For details modal
    const [challengingDreamWeave, setChallengingDreamWeave] = useState(null); // For taunt modal
    const functions = getFunctions();

    // V1: Assume event is active
    const isEventActive = true; 

    useEffect(() => {
        if (!db) {
            setLoading(false);
            return;
        }

        const fetchDreamWeaves = async () => {
            try {
                const dreamWeavesCollection = collection(db, `artifacts/whispers-of-harmony/public/data/dreamWeaves`);
                const q = query(dreamWeavesCollection, where('status', '==', 'active'));
                const querySnapshot = await getDocs(q);
                const allDreamWeaves = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const challengeableWeaves = allDreamWeaves.filter(dw => dw.ownerId !== user.uid);
                setDreamWeaves(challengeableWeaves);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchDreamWeaves();
    }, [db, user]);

    const handleChallenge = async (taunt) => {
        if (!challengingDreamWeave) return;
        const createChallengeFn = httpsCallable(functions, 'createChallenge');
        try {
            const result = await createChallengeFn({ 
                challengedDreamWeaveId: challengingDreamWeave.id, 
                challengerNexusId: user.primaryShowdownNexus,
                taunt: taunt,
            });
            if (result.data.challengeId) {
                startChallenge(result.data.challengeId);
            }
        } catch (error) {
            console.error("Error creating challenge:", error);
        } finally {
            setChallengingDreamWeave(null);
        }
    };

    const sortedDreamWeaves = useMemo(() => {
        return [...dreamWeaves].sort((a, b) => {
            if (sortBy === 'souls') {
                return b.souls - a.souls;
            } else if (sortBy === 'name') {
                return a.title.localeCompare(b.title);
            }
            return 0;
        });
    }, [dreamWeaves, sortBy]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.07 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    if (loading) return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden">
            <div className="flex justify-end items-center mb-4">
                <div className="bg-gray-800/40 backdrop-blur-md text-white p-2 rounded-lg h-10 w-36 animate-pulse" />
            </div>
            <ChallengePageSkeleton />
        </div>
    );
    if (error) return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden items-center justify-center">
            <p className="text-red-500 font-bold">Error loading DreamWeaves.</p>
        </div>
    );

    return (
        <div className="w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden">
            {challengingDreamWeave && (
                <TauntSelectionModal 
                    opponentName={challengingDreamWeave.title}
                    onSend={handleChallenge}
                    onCancel={() => setChallengingDreamWeave(null)}
                />
            )}

            {isEventActive && (
                <div className="bg-purple-900/40 backdrop-blur-md border border-purple-500/50 text-white p-4 rounded-xl mb-8 flex items-center gap-4 shadow-lg shadow-purple-900/20">
                    <Zap className="text-yellow-300" size={24} />
                    <div>
                        <h3 className="font-bold font-orbitron">Soul Rush is Active!</h3>
                        <p className="text-sm text-purple-200">All Soul gains and losses are doubled for this challenge.</p>
                    </div>
                </div>
            )}
            <div className="flex justify-end items-center mb-6">
                <select onChange={(e) => setSortBy(e.target.value)} value={sortBy} className="bg-gray-800/60 backdrop-blur-md border border-white/10 text-white p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all">
                    <option value="souls">Sort by Souls</option>
                    <option value="name">Sort by Name</option>
                </select>
            </div>
            <motion.div 
                className="w-full max-w-4xl mx-auto space-y-4 overflow-y-auto custom-scrollbar pb-20"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {sortedDreamWeaves.map(dw => (
                    <motion.div key={dw.id} variants={itemVariants}>
                        <div className="bg-gray-800/40 backdrop-blur-md border border-white/10 rounded-xl p-5 flex flex-col md:flex-row items-center justify-between hover:bg-gray-800/60 transition-all group shadow-lg">
                            <div className="mb-4 md:mb-0 md:mr-4 flex-grow text-center md:text-left">
                                <h2 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors font-orbitron">{dw.title}</h2>
                                <p className="text-sm text-cyan-300 font-medium">Souls: {dw.souls}</p>
                            </div>
                            <div className="flex gap-3 flex-shrink-0 w-full md:w-auto">
                                <button 
                                    onClick={() => setSelectedDreamWeave(dw)}
                                    className="px-5 py-2.5 bg-gray-700/50 hover:bg-gray-600/50 border border-white/10 rounded-lg font-bold text-sm flex-1 transition-all backdrop-blur-sm"
                                >
                                    Details
                                </button>
                                <button 
                                    onClick={() => setChallengingDreamWeave(dw)}
                                    className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-lg font-bold text-sm flex-1 transition-all shadow-lg shadow-indigo-500/20 border border-white/10 hover:scale-105"
                                >
                                    Challenge
                                </button>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </motion.div>
            {selectedDreamWeave && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setSelectedDreamWeave(null)}>
                    <div className="bg-gray-900/90 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl text-white max-w-lg w-full transform transition-all scale-100" onClick={e => e.stopPropagation()}>
                        <h2 className="text-3xl font-bold mb-4 font-orbitron text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{selectedDreamWeave.title}</h2>
                        <div className="space-y-2 text-gray-300">
                            <p><span className="font-bold text-gray-400">Owner:</span> {selectedDreamWeave.ownerName}</p>
                            <p><span className="font-bold text-gray-400">Souls:</span> {selectedDreamWeave.souls}</p>
                        </div>
                        <button onClick={() => setSelectedDreamWeave(null)} className="mt-8 w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold transition-colors">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};export default ChallengePage;
